import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import HTMLFlipBook from "react-pageflip";
import { Maximize2, X, ChevronLeft, ChevronRight } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Cache the in-flight render promise per URL so concurrent mounts (StrictMode)
// and revisits reuse a single PDF download + render pass. Progress is shared
// via a module map so the loader shows real download+render progress.
const pageCache = new Map();
const progressMap = new Map(); // url -> 0..100

// 1x1 transparent placeholder for off-window pages (no network request).
const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

// Split a rendered spread-canvas into its left & right pages as Blob object URLs.
// Blob URLs decode faster and use far less memory than base64 data URLs.
function halves(canvas) {
  const hw = Math.floor(canvas.width / 2);
  return Promise.all([0, hw].map((off) => new Promise((res) => {
    const hc = document.createElement("canvas");
    hc.width = hw; hc.height = canvas.height;
    hc.getContext("2d").drawImage(canvas, off, 0, hw, canvas.height, 0, 0, hw, canvas.height);
    hc.toBlob((b) => res(URL.createObjectURL(b)), "image/jpeg", 0.82);
  }))); // [left, right]
}

// Decode an image up-front so page-flips never decode on the main thread (no jank).
function preload(src) {
  return new Promise((res) => {
    const im = new Image();
    const done = () => (im.decode ? im.decode().then(res).catch(res) : res());
    im.onload = done;
    im.onerror = res;
    im.src = src;
  });
}

// Render the whole PDF into ordered single-page images. Returns { pages, ratio }.
async function renderMagazine(url) {
  progressMap.set(url, 0);
  const task = pdfjsLib.getDocument({ url, disableAutoFetch: false });
  task.onProgress = (d) => { if (d && d.total) progressMap.set(url, Math.round((d.loaded / d.total) * 45)); };
  const pdf = await task.promise;

  let frontCover = null, backCover = null;
  const interior = [];
  let ratio = 1.376;
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 1.1 });
    const canvas = document.createElement("canvas");
    canvas.width = vp.width; canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
    const [left, right] = await halves(canvas);
    if (i === 1) {
      // Printer's wrap: LEFT = back cover, RIGHT = front cover
      ratio = vp.height / Math.floor(vp.width / 2);
      backCover = left;
      frontCover = right;
    } else {
      interior.push(left, right);
    }
    progressMap.set(url, 45 + Math.round((i / pdf.numPages) * 45));
  }
  // Physical page order: front cover, interior reading spreads, back cover
  const ordered = [frontCover, ...interior, backCover].filter(Boolean);
  // Pre-decode everything so flipping is smooth from the first turn.
  await Promise.all(ordered.map(preload));
  progressMap.set(url, 100);
  return { pages: ordered, ratio };
}

export default function Flipbook({ url, title = "Magazine", pageImages = [] }) {
  const prerendered = Array.isArray(pageImages) && pageImages.length > 0;
  const [pages, setPages] = useState(prerendered ? pageImages : []);
  const [ratio, setRatio] = useState(1.376); // height / width of a single page
  const [status, setStatus] = useState("loading");
  const [pct, setPct] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(-1); // -1 = closed, else spread index
  const bookRef = useRef(null);

  // Path A: pre-rendered page images, no PDF download/render needed (fast).
  // Measure the first page's real aspect ratio BEFORE mounting the flipbook so
  // the leaf shape matches the source (portrait OR landscape) and object-cover
  // never crops the page.
  useEffect(() => {
    if (!prerendered) return;
    setPages(pageImages);
    const im = new Image();
    im.onload = () => {
      if (im.naturalWidth) setRatio(im.naturalHeight / im.naturalWidth);
      setStatus("ready");
    };
    im.onerror = () => setStatus("ready");
    im.src = pageImages[0];
  }, [prerendered, pageImages]);

  // Prefetch pages around the current one so flips stay smooth (no on-flip decode jank).
  useEffect(() => {
    if (!prerendered) return;
    for (let k = -2; k <= 6; k++) {
      const idx = currentPage + k;
      if (idx >= 0 && idx < pageImages.length) {
        const im = new Image();
        im.decoding = "async";
        im.src = pageImages[idx];
      }
    }
  }, [currentPage, prerendered, pageImages]);

  // Path B: fall back to rendering the PDF in the browser.
  useEffect(() => {
    if (prerendered) return;
    let alive = true;
    if (!pageCache.has(url)) pageCache.set(url, renderMagazine(url));
    pageCache.get(url)
      .then(({ pages, ratio }) => {
        if (!alive) return;
        setRatio(ratio); setPages(pages); setStatus("ready");
      })
      .catch(() => {
        pageCache.delete(url);
        if (alive) setStatus("error");
      });
    return () => { alive = false; };
  }, [url, prerendered]);

  // Poll shared render progress while loading
  useEffect(() => {
    if (status !== "loading") return;
    const id = setInterval(() => setPct(progressMap.get(url) || 0), 180);
    return () => clearInterval(id);
  }, [status, url]);

  // Group single half-pages into visual spreads: [cover], [L,R], … , [backCover]
  const spreads = useMemo(() => {
    if (!pages.length) return [];
    // cover alone, then paired reading spreads; a lone final page (odd count)
    // becomes its own back-cover spread.
    const s = [[pages[0]]];
    for (let i = 1; i < pages.length; i += 2) {
      s.push(pages[i + 1] ? [pages[i], pages[i + 1]] : [pages[i]]);
    }
    return s;
  }, [pages]);

  const spreadOf = (page) => {
    if (page <= 0) return 0;
    if (page >= pages.length - 1) return Math.max(0, spreads.length - 1);
    return Math.floor((page - 1) / 2) + 1;
  };

  // Keyboard controls while the zoom lightbox is open
  useEffect(() => {
    if (zoom < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(-1);
      else if (e.key === "ArrowRight") setZoom((z) => Math.min(spreads.length - 1, z + 1));
      else if (e.key === "ArrowLeft") setZoom((z) => Math.max(0, z - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, spreads.length]);

  // Track small screens so the zoom lightbox can enlarge one page at a time.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  if (status === "error")
    return (
      <a href={url} target="_blank" rel="noreferrer" data-cursor="hover"
        className="flex items-center gap-3 rounded-xl border border-border bg-surface p-5 hover:border-lime transition-colors">
        <span className="tag-lime text-[10px] px-2 py-1">PDF</span>
        <span className="font-mono text-xs uppercase tracking-widest text-ink">Open {title} [↗]</span>
      </a>
    );

  if (status === "loading")
    return (
      <div className="flipbook-stage relative flex flex-col items-center justify-center gap-4 rounded-2xl py-16 px-6" data-testid="flipbook-loading">
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted2">Preparing the magazine</div>
        <div className="h-1 w-56 max-w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full bg-lime transition-[width] duration-200 ease-out" style={{ width: `${Math.max(4, pct)}%` }} />
        </div>
        <div className="font-mono text-[11px] tabular-nums text-ink">{pct}%</div>
      </div>
    );

  const W = 430, H = Math.round(W * ratio);

  return (
    <div
      className="flipbook-stage relative flex flex-col items-center gap-6 rounded-2xl py-10 px-4"
      data-lenis-prevent data-testid="flipbook"
    >
      <button
        onClick={() => setZoom(spreadOf(currentPage))}
        data-cursor="hover" data-testid="flip-zoom"
        className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 rounded-full border border-border bg-bg/70 backdrop-blur px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink hover:border-lime hover:text-lime transition-colors"
        aria-label="View full screen"
      >
        <Maximize2 size={13} /> <span className="hidden sm:inline">Full screen</span>
      </button>

      <HTMLFlipBook
        ref={bookRef}
        width={W} height={H}
        size="stretch"
        minWidth={150} maxWidth={520} minHeight={206} maxHeight={980}
        showCover={true}
        usePortrait={false}
        mobileScrollSupport={true}
        maxShadowOpacity={0.5}
        drawShadow={true}
        useMouseEvents={true}
        flippingTime={650}
        swipeDistance={30}
        onFlip={(e) => setCurrentPage(e.data)}
        className="flipbook-book"
        style={{}}
      >
        {pages.map((src, i) => {
          // Only give pages near the current view a real src (plus the two covers);
          // far pages stay a 1x1 placeholder so we don't fetch all 86 images at once.
          const near = !prerendered || i === 0 || i === pages.length - 1 || (i >= currentPage - 6 && i <= currentPage + 10);
          return (
            <div key={i} className="flipbook-page overflow-hidden" data-testid={`flip-page-${i}`}>
              <img
                src={near ? src : BLANK}
                alt={`${title} page ${i + 1}`}
                decoding="async"
                className="w-full h-full object-cover select-none pointer-events-none"
              />
            </div>
          );
        })}
      </HTMLFlipBook>

      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest">
        <button onClick={() => bookRef.current?.pageFlip()?.flipPrev()} data-cursor="hover" data-testid="flip-prev"
          className="rounded-full border border-border px-4 py-2 text-ink hover:border-lime hover:text-lime transition-colors">‹ Prev</button>
        <button onClick={() => setZoom(spreadOf(currentPage))} data-cursor="hover" data-testid="flip-zoom-2"
          className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-muted2 hover:border-lime hover:text-lime transition-colors">
          <Maximize2 size={12} /> Zoom
        </button>
        <button onClick={() => bookRef.current?.pageFlip()?.flipNext()} data-cursor="hover" data-testid="flip-next"
          className="rounded-full border border-border px-4 py-2 text-ink hover:border-lime hover:text-lime transition-colors">Next ›</button>
      </div>

      {zoom >= 0 && spreads[zoom] && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col"
          data-lenis-prevent data-testid="flip-zoom-overlay"
          onClick={() => setZoom(-1)}
        >
          <div className="flex items-center justify-between px-5 md:px-8 py-4 shrink-0">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted2">
              {title}, spread {zoom + 1} / {spreads.length}
            </span>
            <button onClick={() => setZoom(-1)} data-testid="flip-zoom-close"
              className="rounded-full border border-white/20 p-2 text-white hover:border-lime hover:text-lime transition-colors">
              <X size={18} />
            </button>
          </div>

          <div className="relative flex-1 min-h-0 flex items-center justify-center md:gap-3 px-0 md:px-6 pb-6" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setZoom((z) => Math.max(0, z - 1))}
              disabled={zoom === 0}
              data-testid="flip-zoom-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 md:static md:translate-y-0 shrink-0 rounded-full border border-white/20 bg-black/50 backdrop-blur p-2 md:p-3 text-white hover:border-lime hover:text-lime disabled:opacity-30 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>

            <div
              className={`flex items-center gap-0 max-h-full ${isMobile ? "overflow-x-auto snap-x snap-mandatory w-full px-3" : "justify-center max-w-full"}`}
              style={isMobile ? { WebkitOverflowScrolling: "touch" } : undefined}
            >
              {spreads[zoom].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${title} zoom ${i}`}
                  className={`max-h-[82vh] w-auto object-contain shadow-2xl shrink-0 ${isMobile ? "snap-center" : ""}`}
                  style={{ maxWidth: isMobile ? "94vw" : (spreads[zoom].length > 1 ? "45vw" : "88vw") }}
                />
              ))}
            </div>

            <button
              onClick={() => setZoom((z) => Math.min(spreads.length - 1, z + 1))}
              disabled={zoom === spreads.length - 1}
              data-testid="flip-zoom-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 md:static md:translate-y-0 shrink-0 rounded-full border border-white/20 bg-black/50 backdrop-blur p-2 md:p-3 text-white hover:border-lime hover:text-lime disabled:opacity-30 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
